import type { LoginInput, RegisterInput, RegisterResult, UpdateAccountInput, User } from '@sprintdeck/shared';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api.ts';
import { setLocale } from '../../lib/i18n/index.ts';

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: async (): Promise<User | null> => {
    try {
      const response = await api.get<{ user: User }>('/api/auth/me');
      return response.user;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },
  staleTime: 60_000,
});

function useSetUser() {
  const queryClient = useQueryClient();
  return (user: User | null) => {
    queryClient.setQueryData(meQuery.queryKey, user);
    if (user) void setLocale(user.locale);
  };
}

export function useLogin() {
  const setUser = useSetUser();
  return useMutation({
    mutationFn: (input: LoginInput) => api.post<{ user: User }>('/api/auth/login', input),
    onSuccess: (response) => setUser(response.user),
  });
}

export function useRegister() {
  const setUser = useSetUser();
  return useMutation({
    mutationFn: (input: RegisterInput) => api.post<RegisterResult>('/api/auth/register', input),
    onSuccess: (response) => {
      if (response.status === 'ACTIVE') setUser(response.user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/api/auth/logout'),
    onSettled: () => {
      queryClient.clear();
      queryClient.setQueryData(meQuery.queryKey, null);
    },
  });
}

export function useUpdateAccount() {
  const setUser = useSetUser();
  return useMutation({
    mutationFn: (input: UpdateAccountInput) => api.patch<{ user: User }>('/api/account', input),
    onSuccess: (response) => setUser(response.user),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post<void>('/api/account/password', input),
  });
}
